import React from 'react';
import { ShellProvider, useShell } from '../../context/ShellContext';
import TopBar from './TopBar';
import ChannelRail from './ChannelRail';
import RoomPane from './RoomPane';
import DetailPane from './DetailPane';
import ModalManager from './ModalManager';
import SideSheet from './SideSheet';
import './AppShell.css';

function AppShellInner({
  children,
  user,
  profile,
  displayName,
  onSignOut,
  currentView,
  navProps,
  detailProps,
  modalProps,
  hasUnreadNav,
  showSubChatToggle,
  hasUnreadSubChats,
}) {
  const { viewportMode, isNavPinned, navOpen, closeNav, isDetailPinned, detailPaneOpen, closeDetailPane } = useShell();

  const showDetailPane =
    (currentView === 'chats' && detailProps.selectedChat && detailProps.selectedChatAccessRoot) ||
    (currentView === 'dm' && detailProps.selectedFriend);

  const {
    rootChats,
    selectedRootChat,
    selectedChat,
    selectedChatChildren,
    acceptedFriends,
    onlineUserIds,
    unreadChatIds,
    unreadDmIds,
    totalInviteBadge,
    rootChatHasUnread,
    selectedFriend,
    onCreateRoom,
    canCreateSubRoom,
    onOpenCreateSubRoom,
  } = navProps;

  const isChannelOwner = selectedRootChat?.created_by === user?.id;

  const railProps = {
    rootChats: rootChats || [],
    selectedRootChatId: selectedRootChat?.id,
    acceptedFriends: acceptedFriends || [],
    onlineUserIds,
    unreadChatIds,
    unreadDmIds,
    totalInviteBadge,
    currentView,
    rootChatHasUnread,
    selectedFriend,
    onCreateRoom,
  };

  const roomPaneProps = {
    selectedRootChat,
    selectedChatChildren: selectedChatChildren || [],
    selectedChatId: selectedChat?.id,
    unreadChatIds,
    getVoicePresence: navProps.getVoicePresence,
    canCreateSubRoom,
    onOpenCreateSubRoom,
  };

  const navContent = (onNavClose) => (
    <div className="nav-sidesheet-inner">
      <ChannelRail {...railProps} onNavClose={onNavClose} />
      <RoomPane {...roomPaneProps} onNavClose={onNavClose} />
    </div>
  );

  return (
    <div className={`app-shell app-shell--${viewportMode}`}>
      <a href="#main-content" className="skip-nav">Skip to content</a>

      <TopBar
        user={user}
        profile={profile}
        displayName={displayName}
        onSignOut={onSignOut}
        showDetailToggle={showDetailPane}
        hasUnreadNav={hasUnreadNav}
        showSubChatToggle={showSubChatToggle}
        hasUnreadSubChats={hasUnreadSubChats}
        selectedRootChat={selectedRootChat}
        isChannelOwner={isChannelOwner}
        onOpenChannelSettings={modalProps.onOpenChannelSettings}
      />

      <div className="app-body">
        {isNavPinned ? (
          <>
            <ChannelRail {...railProps} />
            <RoomPane {...roomPaneProps} />
          </>
        ) : (
          <SideSheet open={navOpen} onClose={closeNav} side="left" label="Navigation">
            {navContent(closeNav)}
          </SideSheet>
        )}

        <main className="content-area" id="main-content">
          {children}
        </main>

        {showDetailPane && (
          isDetailPinned ? (
            <DetailPane {...detailProps} />
          ) : (
            <SideSheet open={detailPaneOpen} onClose={closeDetailPane} side="right" label="Details">
              <DetailPane {...detailProps} />
            </SideSheet>
          )
        )}
      </div>

      <ModalManager {...modalProps} />
    </div>
  );
}

export default function AppShell(props) {
  return (
    <ShellProvider currentView={props.currentView}>
      <AppShellInner {...props} />
    </ShellProvider>
  );
}
